// ═══════════════════════════════════════════════════════════════
// Brand detection — multi-marque (FoodCostOS / Stockzone)
// ═══════════════════════════════════════════════════════════════
// La marque est détectée depuis le hostname du navigateur.
// Cela permet à un même backend de servir plusieurs identités
// commerciales (FCOS pour restauration, Stockzone pour stock générique).
//
// Override possible : ?brand=stockzone dans l'URL (pratique pour démos
// depuis localhost ou pour tester l'autre brand).
// ═══════════════════════════════════════════════════════════════

const BRANDS = {
  fcos: {
    code: 'fcos',
    name: 'FoodCostOS',
    tagline: 'Gestion food cost & stocks pour la restauration',
    primaryColor: '#0F6B4F', // vert FCOS — INCHANGÉ
    darkColor: '#0F1720',
    logoLogin: '/logo_login.png',
    logoSidebar: '/logo_accueil.png',
    favicon: '/favicon.png',
    manifest: '/manifest.fcos.webmanifest',
    homepageUrl: 'https://foodcostos.app',
    // Modules masqués en sidebar pour cette brand (UI seulement, le backend reste accessible)
    hiddenModules: [],
    // Labels custom pour adapter le vocabulaire au métier
    labels: {
      'nav.recipes': 'Fiches techniques',
      'nav.ingredients': 'Ingrédients',
      'dashboard.kpis.stockValue': 'Valeur du stock',
      'reports.foodCost': 'Food Cost',
    },
  },
  stockzone: {
    code: 'stockzone',
    name: 'Stockzone',
    tagline: 'Gestion de stock multi-sites pour le retail et la distribution',
    // Bleu marine Stockzone (refonte juin 2026 — anciennement vert)
    primaryColor: '#1E407C',         // accent principal
    primaryLightColor: '#2563C8',    // liens / états légers
    primarySoftColor: '#78AAF0',     // fonds, surbrillances douces
    accentColor: '#F08C2E',          // orange en accent secondaire ponctuel
    darkColor: '#0F172A',
    logoLogin: '/stockzone_logo_horizontal.png',
    logoSidebar: '/stockzone_logo_horizontal.png',
    favicon: '/stockzone_favicon_512.png',
    manifest: '/manifest.stockzone.webmanifest',
    homepageUrl: 'https://stockzone.app',
    // HACCP et Rappels (RappelConso = alimentaire) restent resto-only : Stockzone
    // n'est pas alimentaire. Les fiches techniques (recipes) et la production
    // sont réactivées et rebrandées en « produits composés ».
    hiddenModules: ['settings-haccp', 'recalls'],
    // Les valeurs sont des CLÉS i18n alternatives — elles seront re-résolues
    // via t() côté UI pour rester traduites en FR/EN.
    // (cf composables/useBrandedI18n.js et components/layout/Sidebar.vue)
    labels: {
      'nav.ingredients':                   'stockzone.nav.products',
      'nav.recipes':                       'stockzone.nav.composedProducts',
      'recipes.types.DISH':                'stockzone.recipes.types.DISH',
      'recipes.types.SUB_RECIPE':          'stockzone.recipes.types.SUB_RECIPE',
      'recipes.foodCost.ratio':            'stockzone.recipes.foodCost.ratio',
      'dashboard.kpis.stockValue':         'stockzone.dashboard.kpis.inventoryValue',
      'dashboard.kpis.activeIngredients':  'stockzone.dashboard.kpis.activeProducts',
      'dashboard.kpis.belowMin':           'stockzone.dashboard.kpis.belowMinProducts',
      'dashboard.topRatios.title':         'stockzone.dashboard.topRatios.title',
      'dashboard.topRatios.tooltip':       'stockzone.dashboard.topRatios.tooltip',
      'dashboard.topRatios.empty':         'stockzone.dashboard.topRatios.empty',
      'reports.foodCost':                  'stockzone.reports.stockAnalysis',
      'cashmag.mapping.autoMapAndCreate':  'stockzone.cashmag.mapping.autoMapAndCreate',
    },
    // Pictogrammes custom (override des emoji par défaut du sidebar).
    // Clé = nom de la route. Pertinent pour retail vs ingrédients alimentaires.
    icons: {
      'ingredients': '📦', // produit retail (boîte) au lieu de 🥕 (carotte)
      'recipes': '🧩',     // produit composé (assemblage) au lieu de 📋 (fiche technique)
    },
  },
};

// Détection brand. Priorité :
//   1. ?brand=X dans l'URL → override + persiste en localStorage
//   2. localStorage (override persistant entre refresh)
//   3. hostname (stockzone.app vs foodcostos.app)
//   4. Défaut : 'fcos'
const BRAND_STORAGE_KEY = 'fcos.brand';

function detectBrand() {
  try {
    const params = new URLSearchParams(window.location.search);
    const forced = params.get('brand');
    if (forced && BRANDS[forced]) {
      localStorage.setItem(BRAND_STORAGE_KEY, forced);
      return forced;
    }
  } catch { /* SSR / contexte sans window */ }
  try {
    const stored = localStorage.getItem(BRAND_STORAGE_KEY);
    if (stored && BRANDS[stored]) return stored;
  } catch { /* ignore */ }
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  if (host.includes('stockzone')) return 'stockzone';
  return 'fcos';
}

const currentBrandCode = detectBrand();

export const currentBrand = BRANDS[currentBrandCode];

// Helper : récupère la config d'une brand par son code (ou currentBrand si inconnu).
// Utile pour résoudre logo/nom dynamiquement quand le tenant change (impersonation).
export function brandFor(brandCode) {
  return (brandCode && BRANDS[brandCode]) ? BRANDS[brandCode] : currentBrand;
}

// Swap des assets PWA (manifest + theme-color + apple-touch-icon) selon la
// marque — pour que « Ajouter à l'écran d'accueil » prenne le bon nom/icône
// sur foodcostos.app comme sur stockzone.app (build unique, marque au runtime).
function applyPwaAssets(brand) {
  if (typeof document === 'undefined') return;
  const manifest = document.getElementById('app-manifest');
  if (manifest && brand.manifest) manifest.setAttribute('href', brand.manifest);
  const theme = document.getElementById('theme-color');
  if (theme) theme.setAttribute('content', brand.primaryColor);
  const apple = document.getElementById('apple-touch-icon');
  if (apple) apple.setAttribute('href', brand.favicon);
}

// Applique les assets pour une brand donnée (utile quand le tenant change :
// impersonation, login, exitImpersonation). Sans argument → currentBrand.
export function applyBrandAssetsFor(brandCode) {
  const brand = (brandCode && BRANDS[brandCode]) ? BRANDS[brandCode] : currentBrand;
  if (typeof document === 'undefined') return;
  applyPwaAssets(brand);
  let link = document.querySelector('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = brand.favicon;
  document.title = brand.name;
  document.documentElement.setAttribute('data-product', brand.code);
  const root = document.documentElement.style;
  root.setProperty('--brand-primary', brand.primaryColor);
  root.setProperty('--brand-dark', brand.darkColor);
  root.setProperty('--accent', brand.primaryColor);
  if (brand.primaryLightColor) root.setProperty('--accent-light', brand.primaryLightColor);
  else root.removeProperty('--accent-light');
  if (brand.primarySoftColor) root.setProperty('--accent-soft', brand.primarySoftColor);
  else root.removeProperty('--accent-soft');
  if (brand.accentColor) root.setProperty('--accent-2', brand.accentColor);
  else root.removeProperty('--accent-2');
}

// Applique le favicon + le titre + l'attribut data-product (pilote les
// surcharges CSS dans main.css : html[data-product="stockzone"] { --accent: ... })
// + les variables CSS de couleur pour les composants qui les lisent (style inline).
export function applyBrandAssets() {
  if (typeof document === 'undefined') return;
  applyPwaAssets(currentBrand);
  // Favicon
  let link = document.querySelector('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = currentBrand.favicon;
  // Title
  document.title = currentBrand.name;
  // Attribut data-product : pilote les overrides CSS dans main.css.
  // Permet de styliser des éléments brand-aware sans dépendre du JS.
  document.documentElement.setAttribute('data-product', currentBrand.code);
  // Variables CSS — utilisables soit en style inline, soit via classes
  // Tailwind arbitraires bg-[var(--accent)] etc. Les variables nouvelles
  // (--accent-light/-soft) ne sont posées QUE si la brand les définit
  // (Stockzone). FCOS garde son palette Tailwind primary-* inchangé.
  const root = document.documentElement.style;
  root.setProperty('--brand-primary', currentBrand.primaryColor);
  root.setProperty('--brand-dark', currentBrand.darkColor);
  root.setProperty('--accent', currentBrand.primaryColor);
  if (currentBrand.primaryLightColor) root.setProperty('--accent-light', currentBrand.primaryLightColor);
  if (currentBrand.primarySoftColor) root.setProperty('--accent-soft', currentBrand.primarySoftColor);
  if (currentBrand.accentColor) root.setProperty('--accent-2', currentBrand.accentColor);
}

// Helper : un module est-il visible pour cette brand ?
export function isModuleVisible(moduleName) {
  return !currentBrand.hiddenModules.includes(moduleName);
}

// Helper : récupérer un label custom (sinon utilise la clé i18n native)
export function brandLabel(key, fallback) {
  return currentBrand.labels[key] || fallback;
}

// Helper : labels pour une brand spécifique (utile en impersonation,
// où la brand UI ne reflète pas le tenant courant).
export function brandLabelFor(brandCode, key, fallback) {
  const b = BRANDS[brandCode] || currentBrand;
  return b.labels[key] || fallback;
}

// Helper : module visible pour une brand spécifique.
export function isModuleVisibleFor(brandCode, moduleName) {
  const b = BRANDS[brandCode] || currentBrand;
  return !b.hiddenModules.includes(moduleName);
}

// Helper : icône custom (emoji) pour une route donnée selon brand, ou
// fallback sur l'icône par défaut du sidebar. Permet d'avoir 📦 (boîte)
// au lieu de 🥕 (carotte) pour les "produits" Stockzone.
export function brandIconFor(brandCode, routeName, fallback) {
  const b = BRANDS[brandCode] || currentBrand;
  return (b.icons && b.icons[routeName]) || fallback;
}
